export function normalizeRamq(raw: string | null | undefined): string {
  return (raw ?? '').replace(/\s+/g, '').toUpperCase();
}

export type RamqValidation = { isValid: boolean; errorMessage: string | null };

const RAMQ_FORMAT_ERROR =
  'Le format de la RAMQ est invalide. Le format attendu est AAAA99999999.';

// The Quebec RAMQ encodes: chars 0–2 = first 3 letters of last name,
// char 3 = first letter of first name, chars 4–5 = YY, chars 6–7 = MM
// (+50 if female), chars 8–9 = DD. We default to current century unless that
// would yield a future date, in which case we assume previous century.
export type RamqInfo = {
  firstNameInitial: string;
  lastNamePrefix: string;
  dob: string | null;
  sex: 'H' | 'F' | null;
};

export function parseRamq(raw: string | null | undefined): RamqInfo | null {
  const v = normalizeRamq(raw);
  if (!/^[A-Z]{4}\d{8}$/.test(v)) return null;

  const lastNamePrefix = v.slice(0, 3);
  const firstNameInitial = v[3];
  const yy = Number.parseInt(v.slice(4, 6), 10);
  const mmRaw = Number.parseInt(v.slice(6, 8), 10);
  const dd = Number.parseInt(v.slice(8, 10), 10);

  const sex: 'H' | 'F' = mmRaw > 50 ? 'F' : 'H';
  const mm = mmRaw > 50 ? mmRaw - 50 : mmRaw;

  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) {
    return { firstNameInitial, lastNamePrefix, dob: null, sex };
  }

  const nowYear = new Date().getFullYear();
  const currentYY = nowYear % 100;
  const century = yy <= currentYY ? 2000 : 1900;
  const year = century + yy;
  const dob = `${year}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;

  return { firstNameInitial, lastNamePrefix, dob, sex };
}

export function validateRamqFormat(raw: string | null | undefined): RamqValidation {
  const v = normalizeRamq(raw);
  if (!v) return { isValid: true, errorMessage: null };
  if (!/^[A-Z]{4}\d{8}$/.test(v)) {
    return { isValid: false, errorMessage: RAMQ_FORMAT_ERROR };
  }
  return { isValid: true, errorMessage: null };
}
